import React, { Component, Fragment, ReactElement } from 'react';
import InfiniteScrollContainer from 'react-infinite-scroll-container';
import * as KeyCode from '~/utils/KeyCode';
import type SegmentedSelectOption from '../Option/SegmentedSelectOption';

export type SegmentedSelectReactElement = ReactElement<SegmentedSelectOption['props'], SegmentedSelectOption['render']>;
export type FlattenedOptionsResult = {
	option: SegmentedSelectReactElement;
	index: number;
	segmentIndex: number;
};

export function flattenOptions(
	pages: SegmentedSelectReactElement[] | SegmentedSelectReactElement
): FlattenedOptionsResult[] {
	const pageChildren = React.Children.toArray(pages) as SegmentedSelectReactElement[];

	return pageChildren.reduce((result, segment, segmentIndex) => {
		const options = React.Children.toArray(segment.props.children).map((option, index) => ({
			option,
			index,
			segmentIndex,
		}));
		Array.prototype.push.apply(result, options);
		return result;
	}, []);
}

interface SelectedOption {
	value: any;
	parentValue: any;
	prompt: React.ReactNode;
}

interface Props {
	selectedOption: SelectedOption | null;
	header: React.ReactNode;
	content: React.ReactNode;
	children: React.ReactNode;
	hasMore: boolean | null;
	styles: { header: string | null };
	onSelect?(v: { index: number; segmentIndex: number; option: React.ReactNode } | null): void;
	onFetch(page: number): void;
}

interface State {
	selectedOption: { index: number; segmentIndex: number; value: any } | null;
}

export type PageCreateProps = {
	selectedOption: Props['selectedOption'];
	styles: Props['styles'];
	ref: ((ref: SegmentedSelectPage) => SegmentedSelectPage) | null;
	onSelect: Props['onSelect'];
};

export default class SegmentedSelectPage extends Component<Props, State> {
	static defaultProps = {
		selectedOption: null,
		header: null,
		content: null,
		hasMore: null,
		styles: { header: null },
		onSelect: null,
		onFetch: null,
	};

	state: State = {
		selectedOption: null,
	};

	componentDidMount() {
		const { selectedOption, children } = this.props;

		if (selectedOption !== null) {
			this.preselectIndex(
				(options) => options.findIndex((option) => option.option.props.value === selectedOption.value),
				true,
				children
			);
		}
	}

	componentDidUpdate(prevProps: Props) {
		const { selectedOption } = this.props;

		if (selectedOption === prevProps.selectedOption) {
			return;
		}

		if (selectedOption === null) {
			this.setState({ selectedOption: null });
		} else {
			this.preselectIndex(
				(options) => options.findIndex((option) => option.option.props.value === selectedOption.value),
				true,
				this.props.children
			);
		}
	}
	renderBody() {
		const {
			props: { header, content, children, styles, onSelect },
			state: { selectedOption },
		} = this;

		return (
			<Fragment>
				{header ? <div className={styles.header || ''}>{header}</div> : null}
				{React.Children.map(children, (segment, index) =>
					React.cloneElement(segment as React.ReactElement, {
						styles,
						createOptionProps: (
							component: {
								props: any;
							},
							optionIndex: number
						) => {
							const option: {
								option: {
									props: any;
								};
								segmentIndex: number;
								index: number;
							} = {
								index: optionIndex,
								segmentIndex: index,
								option: component,
							};

							return {
								selected:
									selectedOption &&
									selectedOption.index === option.index &&
									selectedOption.segmentIndex === option.segmentIndex,
								styles,
								onClick: () => this.preselectOption(option, true, () => onSelect?.(option)),
							};
						},
					})
				)}
				{content}
			</Fragment>
		);
	}

	preselectOption(
		option: {
			option: {
				props: any;
			};
			segmentIndex: number;
			index: number;
		},
		paginate: boolean,
		onFinished?: () => void
	) {
		const { value } = option.option.props;

		this.setState(
			{
				selectedOption: {
					index: option.index,
					segmentIndex: option.segmentIndex,
					value,
				},
			},
			onFinished
		);
	}

	preselectIndex(
		calculateNextIndex: (options: FlattenedOptionsResult[], calculateSelectedIndex: () => number) => number,
		paginate: boolean,
		pages = this.props.children
	) {
		const { selectedOption } = this.state;
		const options = flattenOptions(pages as SegmentedSelectReactElement);

		const calculateSelectedIndex =
			selectedOption === null
				? () => -1
				: () =>
						options.findIndex(
							(option) => option.index === selectedOption.index && option.segmentIndex === selectedOption.segmentIndex
						);

		const nextIndex = calculateNextIndex(options, calculateSelectedIndex);

		if (nextIndex < 0 || nextIndex >= options.length) {
			return null;
		}

		const option = options[nextIndex];
		this.preselectOption(option, paginate);
		return option;
	}

	handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
		switch (event.keyCode) {
			case KeyCode.UP: {
				event.preventDefault();
				this.preselectIndex((options, calculateSelectedIndex) => calculateSelectedIndex() - 1, false);
				break;
			}

			case KeyCode.DOWN:
				event.preventDefault();
				this.preselectIndex((options, calculateSelectedIndex) => calculateSelectedIndex() + 1, false);
				break;

			case KeyCode.ENTER: {
				event.preventDefault();
				const option = this.preselectIndex((options, calculateSelectedIndex) => calculateSelectedIndex(), true);
				if (option !== null) {
					this.props.onSelect?.(option);
				}
				break;
			}

			case KeyCode.PAGE_UP:
				event.preventDefault();
				this.preselectIndex(() => 0, false);
				break;

			case KeyCode.PAGE_DOWN:
				event.preventDefault();
				this.preselectIndex((options) => options.length - 1, false);
				break;

			default:
			// intentionally blank
		}
	}

	render() {
		const { hasMore, onFetch } = this.props;

		return onFetch !== null ? (
			<InfiniteScrollContainer hasMore={Boolean(hasMore)} loadMore={onFetch} pageStart={0} useWindow={false}>
				{this.renderBody()}
			</InfiniteScrollContainer>
		) : (
			this.renderBody()
		);
	}
}
